// ============================================================================
// ORDERS · Collection — the reads (C3: cards, model · C6: repeats, repeat)
// ============================================================================
// collection_cards(p_season_year) and collection_model(p_dev_id, p_season_year)
// are STABLE SECURITY DEFINER doors with has_orders_access() inside. Each
// answers ONE jsonb envelope: { ok: true, ... } or { ok: false, reason }. This
// file is the only place the two are called; it unwraps `ok` so C4/C5 bind to
// the card and model objects (53 keys, always present, null allowed) and never
// to the envelope. A refusal (42501) or a transport error throws as is; an
// `ok:false` throws a CollectionReadError carrying `reason`.
// C8 · every read takes the line (p_line: 'sun' | 'optic', collection-c8-v1).
// A database without that argument answers PGRST202; the read is then sent
// again without it (Conv #153) and the old signature answers Sun.
// ============================================================================
import { supabase } from '../../../supabase';

export const COLLECTION_SEASON_YEAR = 2027;

/** C8 · the two lines. Anything else reads as Sun — the doors' own default. */
export const COLLECTION_LINES = ['sun', 'optic'];
export const lineOf = (line) => (line === 'optic' ? 'optic' : 'sun');

/** The cards read's cache key — shared by the Collection hooks and the bell's chips (no hooks here: the bell imports this file alone). */
export const cardsQueryKey = (seasonYear, line) => ['collection', 'cards', seasonYear, lineOf(line)];

/** One RPC with the line; on PGRST202 (a database before C8) once more without it. */
async function rpcWithLine(fn, args, line) {
  const res = await supabase.rpc(fn, { ...args, p_line: lineOf(line) });
  if (res.error && res.error.code === 'PGRST202') return supabase.rpc(fn, args);
  return res;
}

export class CollectionReadError extends Error {
  constructor(reason, extra) {
    super(`collection read: ${reason}`);
    this.name = 'CollectionReadError';
    this.reason = reason;
    this.extra = extra || null;
  }
}

function unwrap(res, fn) {
  if (res.error) throw res.error;
  const body = res.data;
  if (!body || typeof body !== 'object') throw new CollectionReadError('empty', { fn });
  if (body.ok !== true) throw new CollectionReadError(body.reason || 'unknown', { fn, ...body });
  return body;
}

/** Every card of the season on one line, in one envelope: { season, synced_at, counts, cards[] }. */
export async function fetchCollectionCards(seasonYear = COLLECTION_SEASON_YEAR, line = 'sun') {
  const res = await rpcWithLine('collection_cards', { p_season_year: seasonYear }, line);
  return unwrap(res, 'collection_cards');
}

/** One development, everything the drawer shows: { card, dev, book_lines, colourways, sheet, desk, exits, overrides, sync }. */
export async function fetchCollectionModel(devId, seasonYear = COLLECTION_SEASON_YEAR, line = 'sun') {
  if (!devId) throw new CollectionReadError('not_found', { fn: 'collection_model', dev_id: null });
  const res = await rpcWithLine('collection_model', { p_dev_id: devId, p_season_year: seasonYear }, line);
  return unwrap(res, 'collection_model');
}

/** C6 · every repeat model of the season on one line, in one envelope: { season, synced_at, counts, repeats[] }. */
export async function fetchCollectionRepeats(seasonYear = COLLECTION_SEASON_YEAR, line = 'sun') {
  const res = await rpcWithLine('collection_repeats', { p_season_year: seasonYear }, line);
  return unwrap(res, 'collection_repeats');
}

/** C6 · one repeat model with its deliveries and history: { repeat, deliveries[], history[] }. */
export async function fetchCollectionRepeat(model, seasonYear = COLLECTION_SEASON_YEAR, line = 'sun') {
  if (!model) throw new CollectionReadError('not_found', { fn: 'collection_repeat', model: null });
  const res = await rpcWithLine('collection_repeat', { p_model: model, p_season_year: seasonYear }, line);
  return unwrap(res, 'collection_repeat');
}
